import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Download, ExternalLink, Filter, Folder, Mail, Search, Send, UserPlus, Users, X } from 'lucide-react'
import PageContainer from '@/components/layout/PageContainer'
import TableCard from '@/components/layout/TableCard'
import Card from '@/components/layout/Card'
import ActionMenu from '@/components/ui/ActionMenu'
import OwnerAvatar from '@/components/OwnerAvatar'
import TypeBadge from '@/features/lists/TypeBadge'
import FolderIcon from '@/features/lists/FolderIcon'
import AddContactsModal from '@/features/lists/AddContactsModal'
import ListFormModal from '@/features/lists/ListFormModal'
import { useListActions } from '@/features/lists/useListActions'
import { useList, useListUsage, useRemoveFromList } from '@/features/lists/useLists'
import { criterionLabel } from '@/features/lists/criteria'
import { useAuthStore } from '@/features/auth/authStore'
import { useFormsIf } from '@/features/forms/useForms'
import { useContactsPage, useFieldDefinitions } from '@/features/contacts/useContacts'
import TableSettingsPanel from '@/components/TableSettingsPanel'
import ListCampaignsCard from '@/features/lists/ListCampaignsCard'
import { downloadListCsv } from '@/features/lists/listExport'
import { DEFAULT_LIST_TABLE, useListTableSettings } from '@/features/lists/listTableSettings'
import { COLUMNS, CUSTOM_COLUMN_PREFIX, PAGE_SIZES, customContactColumns, type ColumnId } from '@/features/contacts/contactsTableSettings'
import { formatCustomFieldValue } from '@/lib/customField'
import type { Contact, FieldDefinition } from '@/api/types'
import type { Member } from '@/api/users'
import { useMembers } from '@/features/team/useTeam'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import { formatShortDate, relativeDay } from '@/lib/dealFormat'
import { pageItems } from '@/lib/pageItems'
import { cn } from '@/lib/cn'

function Tile({ label, children, icon }: { label: string; children: React.ReactNode; icon: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 min-w-0">
      <span className="w-10 h-10 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0">{icon}</span>
      <span className="min-w-0">
        <span className="block text-xs text-gray-500">{label}</span>
        <span className="block text-sm font-semibold text-brand-darkest truncate">{children}</span>
      </span>
    </div>
  )
}

/**
 * One list as its own page: who's in it (searchable, each opening in a new tab), and alongside it what
 * keeps the list up to date (filters, or hand-picked), where it's used and its history — with every
 * action (send, add to a sequence, edit, duplicate, move, export, delete) in reach.
 */
export default function ListDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: list, isError } = useList(id)
  const { data: usage } = useListUsage(id)
  const { data: members } = useMembers()
  const isAdmin = useAuthStore((s) => s.user?.role === 'AGENCY_ADMIN' || s.user?.role === 'PLATFORM_ADMIN')
  const { data: forms } = useFormsIf(isAdmin) // names for "Has filled in <form>" filters
  const memberById = new Map((members ?? []).map((m) => [m.id, m]))
  const remove = useRemoveFromList()
  const [query, setQuery] = useState('')
  const q = useDebouncedValue(query.trim(), 300)
  const [page, setPage] = useState(0)
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(false)
  const actions = useListActions({ onDeleted: () => navigate('/lists'), onDuplicated: (copy) => navigate(`/lists/${copy.id}`) })
  const [tableSettings, setTableSettings] = useListTableSettings()
  const PAGE_SIZE = tableSettings.pageSize
  const { data: contactFieldDefs } = useFieldDefinitions('contact')
  const allColumns = [...COLUMNS, ...customContactColumns(contactFieldDefs)]
  const visibleColumns = tableSettings.columns
    .map((cid) => allColumns.find((c) => c.id === cid))
    .filter((c): c is (typeof allColumns)[number] => !!c)
  const { data: contacts, isFetching } = useContactsPage({ list: id, q: q || undefined, page, size: PAGE_SIZE, sort: 'name', dir: 'asc' })

  if (isError) {
    return (
      <PageContainer width="full">
        <p className="text-sm text-gray-500">
          This list doesn't exist or was deleted. <Link to="/lists" className="text-brand-primary hover:underline">Back to lists</Link>
        </p>
      </PageContainer>
    )
  }
  if (!list) return <PageContainer width="full"><div className="h-40 rounded-xl bg-gray-100 animate-pulse" /></PageContainer>

  const owner = list.ownerUserId ? memberById.get(list.ownerUserId) : undefined
  const creator = list.creatorUserId ? memberById.get(list.creatorUserId) : undefined
  const dynamic = list.listType === 'dynamic'
  const rows = contacts?.items ?? []
  const total = contacts?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const filters = dynamic ? (list.criteria ?? []).map((c) => criterionLabel(c, members, forms)) : []

  return (
    <PageContainer width="full">
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-gray-500 mb-2">
        <Link to="/lists" className="hover:text-brand-primary">Lists</Link>
        {list.folderName && (
          <>
            <span aria-hidden>/</span>
            <Link to={`/lists?folder=${list.folderId}`} className="hover:text-brand-primary">{list.folderName}</Link>
          </>
        )}
        <span aria-hidden>/</span>
        <span className="text-gray-700 truncate">{list.name}</span>
      </nav>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-2xl font-semibold text-brand-darkest break-words">{list.name}</h1>
            <TypeBadge type={list.listType} />
          </div>
          {list.description && <p className="text-sm text-gray-500 mt-0.5">{list.description}</p>}
        </div>
        <div className="flex items-center gap-2">
          {dynamic ? (
            <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50">
              <Filter size={15} /> Edit filters
            </button>
          ) : (
            <button type="button" onClick={() => setAdding(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50">
              <UserPlus size={15} /> Add contacts
            </button>
          )}
          {/* In plain sight: people print a list or send it on (HubSpot's "Export segment"). */}
          <button type="button" onClick={() => void downloadListCsv(list)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50">
            <Download size={15} /> Export
          </button>
          <button type="button" onClick={() => actions.openAction('send', list)} className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark">
            <Send size={15} /> Send
          </button>
          <ActionMenu label={`More actions for ${list.name}`} items={actions.itemsFor(list, { includeView: false })} />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Tile label="Contacts" icon={<Users size={18} />}>{list.memberCount.toLocaleString()}</Tile>
        <Tile label="Folder" icon={<Folder size={18} />}>{list.folderName ?? 'Not in a folder'}</Tile>
        <Tile label="Owner" icon={owner ? <OwnerAvatar name={owner.fullName} photoUrl={owner.photoUrl} size="md" /> : <Users size={18} />}>
          {owner?.fullName ?? 'No owner'}
        </Tile>
        <Tile label="Used in" icon={<Mail size={18} />}>
          {list.sequenceCount > 0 ? `${list.sequenceCount} sequence${list.sequenceCount === 1 ? '' : 's'}` : 'Not used yet'}
        </Tile>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1fr_22rem]">
        <div className="min-w-0 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-brand-darkest">Contacts in this list</h2>
            <div className="flex items-center gap-2 w-full max-w-sm">
              <div className="relative flex-1">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setPage(0) }}
                  placeholder="Search contacts…"
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
                />
              </div>
              {/* Edit columns: any contact field, custom ones included — the same choice as on Contacts. */}
              <TableSettingsPanel<ColumnId>
                allColumns={allColumns}
                columns={tableSettings.columns}
                onColumnsChange={(columns) => setTableSettings({ ...tableSettings, columns })}
                pageSizes={PAGE_SIZES}
                pageSize={tableSettings.pageSize}
                onPageSizeChange={(size) => { setTableSettings({ ...tableSettings, pageSize: size as typeof tableSettings.pageSize }); setPage(0) }}
                onReset={() => setTableSettings(DEFAULT_LIST_TABLE)}
              />
            </div>
          </div>
          <TableCard
            footer={
              total > PAGE_SIZE ? (
                <div className="flex items-center justify-between px-4 py-2.5 text-sm text-gray-500">
                  <span>Showing {page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)} of {total}</span>
                  <div className="flex items-center gap-1">
                    <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30" aria-label="Previous page"><ChevronLeft size={16} /></button>
                    {pageItems(page + 1, pages).map((p, i) =>
                      p === '…' ? <span key={`g${i}`} className="px-1.5">…</span> : (
                        <button key={p} type="button" onClick={() => setPage(p - 1)} aria-current={p === page + 1 ? 'page' : undefined}
                          className={cn('min-w-8 h-8 rounded-lg', p === page + 1 ? 'bg-brand-primary text-white' : 'hover:bg-gray-100')}>{p}</button>
                      ),
                    )}
                    <button type="button" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30" aria-label="Next page"><ChevronRight size={16} /></button>
                  </div>
                </div>
              ) : undefined
            }
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 border-b border-gray-100 bg-gray-50/60">
                  {visibleColumns.map((col) => (
                    <th key={col.id} className={cn('py-2.5 whitespace-nowrap', col.id === 'name' ? 'px-4' : 'px-3')}>
                      {col.id === 'name' ? 'Name' : col.label}
                    </th>
                  ))}
                  <th className="px-3 py-2.5 w-20"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || 'Unnamed contact'
                  const o = c.ownerUserId ? memberById.get(c.ownerUserId) : undefined
                  return (
                    <tr key={c.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/80">
                      {visibleColumns.map((col) =>
                        col.id === 'name' ? (
                          <td key={col.id} className="px-4 py-2.5">
                            <a href={`/contacts/${c.id}`} target="_blank" rel="noreferrer" className="font-medium text-brand-darkest hover:text-brand-primary inline-flex items-center gap-1 whitespace-nowrap">
                              {name} <ExternalLink size={12} className="text-gray-300" />
                            </a>
                          </td>
                        ) : (
                          <td key={col.id} className="px-3 py-2.5 text-gray-600 whitespace-nowrap max-w-[16rem] truncate">
                            {cellText(c, col.id, o, contactFieldDefs ?? [])}
                          </td>
                        ),
                      )}
                      <td className="px-3 py-2.5 text-right">
                        {!dynamic && (
                          <button type="button" onClick={() => remove.mutate({ listId: list.id, contactId: c.id })} className="p-1 text-gray-300 hover:text-danger" title={`Remove ${name} from this list`}>
                            <X size={15} />
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {!isFetching && rows.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-gray-500">
                {q ? 'No contacts in this list match.' : dynamic ? 'Nobody matches the filters yet.' : (
                  <>
                    This list is empty.{' '}
                    <button type="button" onClick={() => setAdding(true)} className="text-brand-primary hover:underline">Add contacts</button>
                  </>
                )}
              </div>
            )}
          </TableCard>
        </div>

        <div className="space-y-4">
          {dynamic ? (
            <Card title="Filters" action={<button type="button" onClick={() => setEditing(true)} className="text-xs font-medium text-brand-primary">Edit</button>}>
              <p className="text-xs text-gray-500 mb-2">Contacts join and leave this list automatically when they match all of these.</p>
              {filters.length === 0 && <p className="text-sm text-gray-600">No filters — every contact you can see.</p>}
              <ol className="space-y-1.5">
                {filters.map((f, i) => (
                  <li key={i}>
                    {i > 0 && <div className="text-[10px] font-semibold text-gray-400 tracking-wider text-center">AND</div>}
                    <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-sm text-gray-700">{f}</div>
                  </li>
                ))}
              </ol>
            </Card>
          ) : (
            <Card title="Static list">
              <p className="text-sm text-gray-600">Contacts are added and removed by hand.</p>
              <button type="button" onClick={() => setAdding(true)} className="mt-2 text-sm font-medium text-brand-primary">+ Add contacts</button>
            </Card>
          )}

          <Card title="Used in" action={<button type="button" onClick={() => actions.openAction('sequence', list)} className="text-xs font-medium text-brand-primary">Add to sequence</button>}>
            {usage && usage.length > 0 ? (
              <ul className="space-y-2">
                {usage.map((u) => (
                  <li key={u.sequenceId} className="flex items-center gap-2.5">
                    <Mail size={15} className="text-brand-primary shrink-0" />
                    <span className="min-w-0">
                      <Link to="/drip" className="block text-sm font-medium text-brand-darkest hover:text-brand-primary truncate">{u.sequenceName}</Link>
                      <span className="block text-xs text-gray-500">Sequence · enrolled {formatShortDate(u.lastEnrolledAt)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">Not used in a sequence yet.</p>
            )}
          </Card>

          <ListCampaignsCard listId={list.id} />

          <Card title="Details">
            <dl className="text-sm space-y-2">
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Folder</dt>
                <dd className="text-gray-700 inline-flex items-center gap-1.5">
                  {list.folderName ? <><FolderIcon color={list.folderColor} /> {list.folderName}</> : '—'}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Created</dt>
                <dd className="text-gray-700 text-right">{formatShortDate(list.createdAt)}{creator ? ` by ${creator.fullName}` : ''}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Updated</dt>
                <dd className="text-gray-700">{formatShortDate(list.updatedAt)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Last campaign</dt>
                <dd className="text-gray-700">{list.lastSentAt ? formatShortDate(list.lastSentAt) : 'Never sent'}</dd>
              </div>
            </dl>
          </Card>
        </div>
      </div>

      {adding && <AddContactsModal list={list} onClose={() => setAdding(false)} />}
      {editing && <ListFormModal list={list} onClose={() => setEditing(false)} />}
      {actions.dialog}
    </PageContainer>
  )
}

/** One cell of the list's table as text (names, numbers, dates) — "—" when the contact has nothing there. */
function cellText(c: Contact, column: ColumnId, owner: Member | undefined, defs: FieldDefinition[]): React.ReactNode {
  const none = <span className="text-gray-300">—</span>
  switch (column) {
    case 'email':
      return c.email || none
    case 'mobile':
      return c.mobile || none
    case 'phone':
      return c.phone || none
    case 'owner':
      return owner ? (
        <span className="inline-flex items-center gap-1.5 text-gray-700"><OwnerAvatar name={owner.fullName} photoUrl={owner.photoUrl} /> {owner.fullName}</span>
      ) : none
    case 'source':
      return c.leadSource || none
    case 'lastActivity':
      return c.lastContactedAt ? relativeDay(c.lastContactedAt) : 'Never'
    case 'nextContact':
      return c.nextContactDate ? formatShortDate(c.nextContactDate) : none
    case 'openDeals':
      return c.openDeals
    case 'created':
      return formatShortDate(c.createdAt)
    default: {
      if (!column.startsWith(CUSTOM_COLUMN_PREFIX)) return none
      const def = defs.find((d) => d.fieldKey === column.slice(CUSTOM_COLUMN_PREFIX.length))
      const text = def ? formatCustomFieldValue(def, c.custom?.[def.fieldKey]) : null
      return text ?? none
    }
  }
}
